import _ from "lodash";

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import createSagaMiddleware, { type SagaMiddlewareOptions } from "redux-saga";

import { alertReducer } from "kit/alert";

import sharedReducer from "../containers/Shared/Shared.slice";
import appReducer from "../containers/App/App.slice";
import confirmReducer from "../containers/Confirm/Confirm.slice";
import accountReducer from "../containers/Account/Account.slice";
import addressDetailsReducer from "../containers/AddressDetails/AddressDetails.slice";
import consentAuthorizationReducer from "../containers/ConsentAuthorization/ConsentAuthorization.slice";
import creditHistoryReducer from "../containers/CreditHistory/CreditHistory.slice";
import creditSummaryReducer from "../containers/CreditSummary/CreditSummary.slice";
import dashboardReducer from "../containers/Dashboard/Dashboard.slice";
import directBankTransferReducer from "../containers/DirectBankTransfer/DirectBankTransfer.slice";
import documentsReducer from "../containers/Documents/Documents.slice";
import financialInfoReducer from "../containers/FinancialInfo/FinancialInfo.slice";
import handoverReducer from "../containers/Handover/Handover.slice";
import homeReducer from "../containers/Home/Home.slice";
import loanApplyReducer from "../containers/LoanApply/LoanApply.slice";
import loanFlowReducer from "../containers/LoanFlow/LoanFlow.slice";
import loanRequestReducer from "../containers/LoanRequest/LoanRequest.slice";
import loginReducer from "../containers/Login/Login.slice";
import otpReducer from "../containers/Otp/Otp.slice";
import paymentReducer from "../containers/Payment/Payment.slice";
import paymentReasonReducer from "../containers/PaymentReason/PaymentReason.slice";
import registerReducer from "../containers/Register/Register.slice";
import restructureReducer from "../containers/Restructure/Restructure.slice";
import rolloverReducer from "../containers/Rollover/Rollover.slice";
import threeDSecureReducer from "../containers/ThreeDSecure/ThreeDSecure.slice";
import loanCalculatorReducer from "../containers/LoanCalculator/LoanCalculator.slice";
import shortlinkReducer from "../containers/Shortlink/Shortlink.slice";

import ACTION_DENYLIST_FOR_DEVTOOLS from "./actionsDenylistForDevtools";
import SERIALIZABLE_CHECK_IGNORED_ACTIONS from "./serializableCheckIgnoredActions";

// Create saga middleware with optional monitor in dev
const sagaMiddlewareOptions: SagaMiddlewareOptions = {
  onError: (error, errorInfo) => console.log(error, errorInfo)
};

// Dynamically load and configure saga monitor in dev environment
if (import.meta.env.DEV) {
  const { createConditionalSagaMonitor, setupSagaMonitorControls } = await import("./sagaMonitor");
  sagaMiddlewareOptions.sagaMonitor = createConditionalSagaMonitor();
  setupSagaMonitorControls();
}

const sagaMiddleware = createSagaMiddleware(sagaMiddlewareOptions);

const rootReducer = combineReducers({
  app: appReducer,
  shared: sharedReducer,
  confirm: confirmReducer,
  alerts: alertReducer,
  account: accountReducer,
  addressDetails: addressDetailsReducer,
  consentAuthorization: consentAuthorizationReducer,
  creditHistory: creditHistoryReducer,
  creditSummary: creditSummaryReducer,
  dashboard: dashboardReducer,
  directBankTransfer: directBankTransferReducer,
  documents: documentsReducer,
  financialInfo: financialInfoReducer,
  handover: handoverReducer,
  home: homeReducer,
  loanApply: loanApplyReducer,
  loanFlow: loanFlowReducer,
  loanRequest: loanRequestReducer,
  login: loginReducer,
  otp: otpReducer,
  payment: paymentReducer,
  paymentReason: paymentReasonReducer,
  register: registerReducer,
  restructure: restructureReducer,
  rollover: rolloverReducer,
  threeDSecure: threeDSecureReducer,
  loanCalculator: loanCalculatorReducer,
  shortlink: shortlinkReducer
});

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware({
      thunk: false,
      serializableCheck: {
        ignoredActions: SERIALIZABLE_CHECK_IGNORED_ACTIONS
      }
    }).concat(sagaMiddleware);

    return middleware;
  },
  // Only enable redux dev tools for development environment
  devTools: (import.meta.env.PROD && import.meta.env.MODE === "production") ? false : {
    name: "mykredit",
    actionsDenylist: ACTION_DENYLIST_FOR_DEVTOOLS
  }
});

export type ExtendedStore = typeof store & {
  runSaga: typeof sagaMiddleware.run;
  injectedSagas: Record<string, any>;
};

const extendedStore = store as ExtendedStore;
extendedStore.runSaga = sagaMiddleware.run;
extendedStore.injectedSagas = {};

export default extendedStore;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof extendedStore.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof extendedStore.dispatch;
